import { useState } from 'react';
import PanneauApercu from '../pages/PanneauApercu';
import { IconeOeil, IconeTelecharger } from './IconesDocument';
import {
  genererDocument,
  regenererPdfDocument,
  telechargerPdfDocument,
  urlPdfDocument,
} from '../services/sessionService';
import { formatDateTime } from '../pages/devisUtils';
import { toast } from './Toasts';

/**
 * Le contrat du formateur pour UNE journée : son état, et ses trois gestes —
 * voir, télécharger, générer. Partagé par Formateurs > Contrats des formateurs
 * et par l'onglet « Contrats » de la fiche d'un formateur.
 *
 * Le contrat naît avec la planification (dès qu'un formateur est assigné à la
 * journée) ; « Générer » ne sert donc qu'à le produire quand il manque, ou à le
 * refaire après une correction de la fiche. Le formateur lui-même, depuis son
 * espace, ne fait que le lire (`peutGenerer` faux).
 *
 * `document` : { id, le } quand le contrat est produit, null sinon.
 */
function ContratFormateurActions({ sessionId, document = null, titre, sousTitre, peutGenerer = true, onChange }) {
  const [apercu, setApercu] = useState(false);
  const [travail, setTravail] = useState(null);
  // Change à chaque régénération : le cadre recharge au lieu de garder l'ancien PDF.
  const [version, setVersion] = useState(0);

  async function telecharger() {
    setTravail('dl');
    try {
      await telechargerPdfDocument(document.id);
    } catch (err) {
      toast.erreur(err.message || 'Téléchargement impossible.');
    } finally {
      setTravail(null);
    }
  }

  async function generer() {
    setTravail('gen');
    try {
      await genererDocument(sessionId, 'contrat_formateur');
      toast.succes('Contrat du formateur généré.');
      onChange?.();
    } catch (err) {
      toast.erreur(err.message || 'Génération impossible.');
    } finally {
      setTravail(null);
    }
  }

  async function regenerer() {
    setTravail('regen');
    try {
      const res = await regenererPdfDocument(document.id);
      setVersion((v) => v + 1);
      toast.succes(res.message || 'PDF refait.');
      onChange?.();
    } catch (err) {
      toast.erreur(err.message || 'Régénération impossible.');
    } finally {
      setTravail(null);
    }
  }

  return (
    <div className="contrat-formateur">
      <span className="fg-texte contrat-formateur-etat">
        {document
          ? document.le
            ? `généré le ${formatDateTime(document.le)}`
            : 'généré'
          : 'pas encore généré'}
      </span>
      <div className="contrat-formateur-boutons">
        {document && (
          <>
            <button
              type="button"
              className="devis-btn devis-btn-sm devis-btn-fichier"
              onClick={() => setApercu(true)}
              title="Voir le contrat du formateur"
            >
              <IconeOeil />
              Voir
            </button>
            <button
              type="button"
              className="devis-btn devis-btn-sm devis-btn-fichier"
              onClick={telecharger}
              disabled={travail === 'dl'}
              title="Télécharger le contrat du formateur"
            >
              {travail === 'dl' ? (
                'En cours'
              ) : (
                <>
                  <IconeTelecharger />
                  PDF
                </>
              )}
            </button>
          </>
        )}
        {!document && peutGenerer && (
          <button
            type="button"
            className="devis-btn devis-btn-sm devis-btn-primary"
            onClick={generer}
            disabled={travail === 'gen'}
            title="Produire le contrat du formateur pour cette journée"
          >
            {travail === 'gen' ? 'En cours' : 'Générer'}
          </button>
        )}
      </div>

      {apercu && document && (
        <PanneauApercu
          titre={titre || 'Contrat formateur'}
          sousTitre={sousTitre}
          url={`${urlPdfDocument(document.id, { apercu: true })}&v=${version}`}
          onClose={() => setApercu(false)}
          actions={
            <>
              {peutGenerer && (
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={regenerer}
                  disabled={travail === 'regen'}
                  title="Refaire le PDF à partir de la fiche du formateur et de la journée"
                >
                  {travail === 'regen' ? 'Régénération' : 'Régénérer le PDF'}
                </button>
              )}
              <button
                type="button"
                className="devis-btn devis-btn-sm devis-btn-fichier"
                onClick={telecharger}
                disabled={travail === 'dl'}
              >
                {travail === 'dl' ? 'En cours' : (<><IconeTelecharger />PDF</>)}
              </button>
            </>
          }
        />
      )}
    </div>
  );
}

export default ContratFormateurActions;
